import { keyframes, styled } from "@mui/material/styles";

export const Hero = styled("div")(() => ({
  textAlign: "center",
  marginBottom: 48,
  paddingTop: 40,
}));

export const HeroTitle = styled("h1")(() => ({
  fontSize: 36,
  fontWeight: 700,
  marginBottom: 16,
}));

export const HeroDescription = styled("p")(() => ({
  fontSize: 18,
  color: "#4b5563",
  maxWidth: 680,
  margin: "0 auto",
}));

export const Tabs = styled("div")(() => ({
  display: "inline-flex",
  background: "#fff",
  padding: 6,
  borderRadius: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,.1)",
  gap: 4,
}));

export const TabButton = styled("button")<{
  active?: boolean;
}>(({ active }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "12px 24px",
  borderRadius: 8,
  border: "none",
  cursor: "pointer",
  fontWeight: 500,
  background: active
    ? "linear-gradient(135deg, #9333ea 0%, #2563eb 100%)"
    : "transparent",
  color: active ? "#fff" : "#4b5563",
  backgroundSize: "200% 200%",
  backgroundPosition: "left center",
  transition: "background-position 0.4s ease, transform 0.2s ease",

  "&:hover": {
    backgroundPosition: "right center",
    // transform: "translateY(-1px)",
  },
  whiteSpace: "nowrap",
}));

export const TextArea = styled("textarea")(() => ({
  width: "100%",
  height: 256,
  padding: "12px 16px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  background: "#f9fafb",
  fontSize: 16,
  resize: "none",
  fontFamily: "inherit",

  "&:focus": {
    outline: "none",
    borderColor: "#9333ea",
    boxShadow: "0 0 0 3px rgba(147,51,234,.1)",
  },
}));

export const PrimaryButton = styled("button")(() => ({
  padding: "12px 32px",
  borderRadius: 8,
  border: "none",
  color: "#fff",
  fontWeight: 600,
  textTransform: "uppercase",
  cursor: "pointer",
  background: "linear-gradient(135deg, #9333ea 0%, #2563eb 100%)",
  transition: "all .3s",
  fontSize: "14px",
  display: "inline-flex",
  gap: "6px",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(147,51,234,.4)",
  },

  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
    transform: "none",
  },
}));

export const UploadArea = styled("div")(() => ({
  border: "2px dashed #d1d5db",
  borderRadius: 12,
  padding: 48,
  textAlign: "center",
  transition: "all .3s",
  marginTop: 24,

  "&:hover": {
    borderColor: "#9333ea",
  },
}));

export const UploadIcon = styled("div")(() => ({
  width: 64,
  height: 64,
  borderRadius: "50%",
  background: "#f3e7ff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 16px",
  color: "#9333ea",
  fontSize: 28,
}));

interface UploadButtonProps {
  isDisabled?: boolean;
}

export const UploadButton = styled("label")<UploadButtonProps>(
  ({ isDisabled }) => ({
    display: "inline-flex",
    gap: "5px",
    marginTop: 12,
    padding: "12px 32px",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 500,
    color: "#fff",
    background: !isDisabled
      ? "linear-gradient(135deg, #9333ea 0%, #2563eb 100%)"
      : "#c3c3c3",
    transition: "all .3s",

    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(147,51,234,.4)",
    },
  }),
);

export const FileItem = styled("div")(() => ({
  marginTop: "15px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 16,
  borderRadius: 12,
  background: "#f9fafb",
  border: "1px solid #e5e7eb",
}));

export const FileHeader = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

export const FileInfo = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 4,
  "& i": {
    fontSize: "13px",
    color: "#f30701",
  },
}));

export const Features = styled("div")(() => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 16,
  paddingTop: 24,
  marginTop: 32,
  borderTop: "1px solid #e5e7eb",

  "@media (max-width: 640px)": {
    gridTemplateColumns: "1fr",
  },
}));

export const FeatureItem = styled("div")(() => ({
  display: "flex",
  gap: 12,
  alignItems: "flex-start",
}));

export const FeatureIcon = styled("div")(() => ({
  width: 40,
  height: 40,
  borderRadius: 8,
  background: "#f3e7ff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
  fontWeight: 600,
  color: "#9333ea",
  flexShrink: 0,
}));

export const FeatureContent = styled("div")(() => ({
  "& h4": {
    fontWeight: 600,
    marginBottom: 4,
  },
  "& p": {
    fontSize: 14,
    color: "#6b7280",
  },
}));

export const FeatureH4 = styled("h4")(() => ({
  marginTop: 0,
  marginBottom: 0,
}));
export const FeatureP = styled("p")(() => ({
  marginTop: 0,
  marginBottom: 0,
}));

export const SectionMain = styled("div")(({ theme }) => ({
  backgroundColor: "#ffffff",
  padding: "32px",
  borderRadius: "16px",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.1)",
  marginTop: "20px",
  marginBottom: "40px",
  [theme.breakpoints.down("sm")]: {
    padding: "16px",
  },
}));

export const SectionHeader = styled("div")(() => ({
  marginBottom: 0,
}));

export const SectionTitle = styled("h3")(() => ({
  margin: 0,
  fontSize: 24,
  fontWeight: 700,
}));

export const SectionDescription = styled("p")(() => ({
  color: "#4b5563",
  fontSize: 16,
}));

// export const Select = styled("select")(() => ({
//   width: "100%",
//   padding: 12,
//   borderRadius: 8,
//   background: "#f9fafb",
//   border: "1px solid #e5e7eb;",
// }));

export const SelectWrapper = styled("div")(() => ({
  position: "relative",
  width: "100%",
}));

export const Select = styled("select")(() => ({
  width: "100%",
  height: 44,
  padding: "8px 40px 8px 12px",
  borderRadius: 6,
  backgroundColor: "#ffffff",
  border: "1px solid #d1d5db",
  fontSize: 14,
  color: "#111827",
  cursor: "pointer",
  appearance: "none",
  transition: "all 0.15s ease-in-out",

  boxShadow: "inset 0 1px 1px rgba(0,0,0,0.075)",

  ":hover": {
    borderColor: "#9ca3af",
  },

  // ":focus": {
  //   outline: "none",
  //   borderColor: "#3b82f6",
  //   boxShadow:
  //     "inset 0 1px 1px rgba(0,0,0,0.075), 0 0 0 2px rgba(59,130,246,0.2)",
  // },

  ":disabled": {
    backgroundColor: "#f3f4f6",
    cursor: "not-allowed",
  },
}));

export const SelectArrow = styled("span")(() => ({
  position: "absolute",
  top: "50%",
  right: 10,
  width: 20,
  height: 20,
  transform: "translateY(-50%)",
  pointerEvents: "none",
  color: "#6b7280",

  "& svg": {
    width: "100%",
    height: "100%",
  },
}));

/* Language row */
export const LanguageRow = styled("div")(() => ({
  flex: 1,
}));

export const Arrow = styled("div")(() => ({
  textAlign: "center",
  fontSize: 20,
}));

/* Form */
export const FormGroup = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  gap: "1rem",
  marginBottom: "1.5rem",
}));

export const Label = styled("label")(() => ({
  fontWeight: 500,
  marginBottom: "0.5rem",
  display: "block",
  fontSize: "0.875rem",
}));

export const BtnSwap = styled("button")(() => ({
  padding: "0.6rem",
  background: "#f3e7ff",
  color: "#9333ea",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "all 0.3s",
  transform: "rotate(90deg)",
  "&:hover": {
    background: " #e9d5ff",
  },
}));

export const WaveProgressWrapper = styled("div")(() => ({
  marginTop: 16,
}));

export const WaveTitle = styled("div")(() => ({
  fontSize: 14,
  marginBottom: 6,
}));

export const WaveProgress = styled("div")(() => ({
  position: "relative",
  height: 12,
  width: "100%",
  backgroundColor: "#e5e7eb",
  borderRadius: 999,
  overflow: "hidden",
}));

export const WaveProgressFill = styled("div")<{ done?: boolean }>(
  ({ done }) => ({
    height: "100%",
    borderRadius: 999,
    background: `
      repeating-linear-gradient(
        135deg,
        #4f46e5 0px,
        #4f46e5 10px,
        #6366f1 10px,
        #6366f1 20px,
        #818cf8 20px,
        #818cf8 30px
      )
    `,
    backgroundSize: "200% 100%",
    //animation: done ? "none" : "waveStripe 4.2s linear infinite",

    transition: "none", // 👈 rất quan trọng

    boxShadow: "0 0 6px rgba(99,102,241,0.6)",

    "@keyframes waveStripe": {
      "0%": { backgroundPosition: "200% 0" },
      "100%": { backgroundPosition: "-200% 0" },
    },
  }),
);

export const WavePercent = styled("div")(() => ({
  marginTop: 6,
  fontSize: 12,
  color: "#475569",
}));

export const PreviewOverlay = styled("div")(() => ({
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1300,
}));

export const PreviewOverlayPreview = styled("div")(() => ({
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1300,
}));

export const PreviewModal = styled("div")(({ theme }) => ({
  width: "90%",
  maxWidth: 900,
  backgroundColor: "#ffffff",
  borderRadius: 16,
  padding: 20,
  boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
  display: "flex",
  flexDirection: "column",

  [theme.breakpoints.down("sm")]: {
    transform: "scale(0.9)",
  },
}));
export const PreviewHeader = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingBottom: 12,
  borderBottom: "1px solid #e5e7eb",
}));
export const PreviewTitle = styled("h3")(() => ({
  margin: 0,
  fontSize: 18,
}));
export const CloseButton = styled("button")(() => ({
  border: "none",
  background: "transparent",
  fontSize: 20,
  cursor: "pointer",
  lineHeight: 1,
}));
export const PreviewContent = styled("div")(() => ({
  marginTop: 16,
  maxHeight: "65vh",
  overflow: "auto",
  backgroundColor: "#f8fafc",
  padding: 16,
  borderRadius: 8,
}));
export const PreviewCanvas = styled("canvas")(() => ({
  width: "100%",
  backgroundColor: "#ffffff",
  borderRadius: 8,
  boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
}));
export const PreviewFooter = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "end",
  marginTop: 16,
  gap: 8,

  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
  },

  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
  },

  "& .footer-item-i": {
    display: "flex",
    alignItems: "center",
    gap: 5,
    [theme.breakpoints.down("sm")]: {
      // flexDirection: "column",
    },
    [theme.breakpoints.down("lg")]: {
      // flexDirection: "column",
    },
  },
  "& .footer-item-div": {
    display: "flex",
    gap: 6,
    marginLeft: "auto",

    [theme.breakpoints.down("sm")]: {
      display: "grid",
      marginLeft: "unset",
    },
    [theme.breakpoints.down("lg")]: {
      display: "grid",
      marginLeft: "unset",
    },
  },
}));

export const ButtonSaveFileModal = styled("button")(() => ({
  display: "flex",
  gap: 5,
  width: "fixContent",
  padding: "14px 20px",
  justifyContent: "center",
  background: "#fcf5fd",
  color: "#6f4572",
  border: "2px solid #6f4572",
  borderRadius: 12,
  fontSize: 14,
  fontWeight: "600",
  cursor: "pointer",
  transition: "background-color 0.2s",
  // "&:hover": {
  //   border: "2px solid green",
  // },
  "&:disabled": {
    backgroundColor: "#f0f0f0ff",
    color: "#8b8484ff",
    borderColor:
      "light-dark(rgba(118, 118, 118, 0.3), rgba(195, 195, 195, 0.3))",
  },
}));

export const ButtonPaymentModal = styled("button")(() => ({
  display: "flex",
  gap: 5,
  width: "fixContent",
  padding: "12px 16px",
  fontWeight: "600",
  justifyContent: "center",
  alignItems: "center",
  background: "linear-gradient(135deg, #9333ea 0%, #2563eb 100%)",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  fontSize: 14,
  cursor: "pointer",
  transition: "background-color 0.2s",
  "&:hover": {
    backgroundColor: "#1d4ed8",
  },
}));

export const ButtonCloseModal = styled("button")(() => ({
  width: "fixContent",
  padding: "12px 18px",
  backgroundColor: "red",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  fontSize: 14,
  cursor: "pointer",
  transition: "background-color 0.2s",

  "&:disabled": {
    backgroundColor: "#f0f0f0ff",
    color: "#8b8484ff",
    borderColor:
      "light-dark(rgba(118, 118, 118, 0.3), rgba(195, 195, 195, 0.3))",
  },
}));

export const PriceTag = styled("div")(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,

  padding: "10px 16px",
  borderRadius: 12,

  background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
  color: "#ffffff",

  fontSize: 14,
  fontWeight: 600,
  letterSpacing: "0.3px",

  boxShadow: "0 8px 20px rgba(37, 99, 235, 0.25)",

  "& span": {
    fontSize: 13,
    fontWeight: 500,
    opacity: 0.9,
  },
}));

export const DiscountBox = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: 12,

  padding: "12px 18px",
  borderRadius: 14,

  background: "linear-gradient(135deg, #16a34a, #15803d)",
  color: "#ffffff",

  fontSize: 14,
  fontWeight: 600,

  boxShadow: "0 10px 25px rgba(22, 163, 74, 0.35)",

  position: "relative",
  overflow: "hidden",

  "&::after": {
    content: '""',
    position: "absolute",
    top: -20,
    right: -20,
    width: 80,
    height: 80,
    background: "rgba(255,255,255,0.12)",
    borderRadius: "50%",
  },
}));

export const OldPrice = styled("span")(() => ({
  fontSize: 13,
  textDecoration: "line-through",
  opacity: 0.85,
}));

export const NewPrice = styled("span")(() => ({
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: "0.3px",
}));

export const Button = styled("button")(() => ({
  padding: "14px 0",
  backgroundColor: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 12,
  fontSize: 16,
  cursor: "pointer",
  transition: "background-color 0.2s",
  "&:hover": {
    backgroundColor: "#1d4ed8",
  },
  "&:disabled": {
    backgroundColor: "#f0f0f0ff",
    color: "#8b8484ff",
    borderColor:
      "light-dark(rgba(118, 118, 118, 0.3), rgba(195, 195, 195, 0.3))",
  },
}));

export const FileActions = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,

  [theme.breakpoints.down("sm")]: {
    display: "unset",
  },
}));

export const PreviewButton = styled(Button)(({ theme }) => ({
  padding: "4px 10px",
  minWidth: "unset",
  fontSize: 13,
  lineHeight: 1.4,
  borderRadius: 6,
  textTransform: "none",
  background: "linear-gradient(135deg, #9333ea 0%, #2563eb 100%)",
  color: "#ffffff",
  cursor: "pointer",
  height: "30px",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(147,51,234,.4)",
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    whiteSpace: "nowrap",
  },
}));

export const RemoveButton = styled("a")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "30px",
  padding: "0px 10px",
  minWidth: "unset",
  gap: 5,
  fontSize: 13,
  lineHeight: 1.4,
  borderRadius: 6,
  textTransform: "none",
  background: "red",
  color: "#ffffff",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(147,51,234,.4)",
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",

    marginTop: "5px",
  },
}));

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

export const LoadingText = styled("span")(() => ({
  marginLeft: 10,
  fontSize: 14,
  fontWeight: 500,
  animation: `${pulse} 1.2s ease-in-out infinite`,
}));

export const LoadingButtonContent = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

export const LoadingOverlay = styled("div")(() => ({
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
}));

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const Spinner = styled("div")(() => ({
  width: 20,
  height: 20,
  border: "3px solid rgba(255,255,255,0.3)",
  borderTop: "3px solid #ffffff",
  borderRadius: "50%",
  animation: `${spin} 0.8s linear infinite`,
}));

export const FilePreview = styled("div")(() => ({
  marginTop: 12,
  fontSize: 14,
  color: "#2563eb",
  fontWeight: 500,
}));

export const Note = styled("div")(() => ({
  marginBottom: 48,
}));

export const DocumentNoteWrapper = styled("article")(() => ({
  marginBottom: 60,
  padding: "40px 48px",
  borderRadius: 16,

  background: "#ffffff",
  border: "1px solid #eef0f3",
  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",

  marginLeft: "auto",
  marginRight: "auto",

  fontSize: 17,
  lineHeight: 1.8,
  color: "#1f2937",
  fontFamily: `"Inter", "Segoe UI", Roboto, sans-serif`,

  /* ===== HEADINGS ===== */
  "& h1, & h2, & h3, & h4": {
    fontWeight: 700,
    lineHeight: 1.3,
    marginTop: 32,
    marginBottom: 12,
    color: "#111827",
  },

  "& h1": {
    fontSize: 34,
    marginTop: 0,
  },

  "& h2": {
    fontSize: 26,
    borderBottom: "1px solid #f1f5f9",
    paddingBottom: 6,
  },

  "& h3": {
    fontSize: 20,
  },

  "& h4": {
    fontSize: 18,
    color: "#374151",
  },

  /* ===== PARAGRAPH ===== */
  "& p": {
    margin: "14px 0",
  },

  /* ===== LIST ===== */
  "& ul, & ol": {
    paddingLeft: 24,
    margin: "16px 0",
  },

  "& li": {
    marginBottom: 8,
  },

  "& ol li[data-list='bullet']": {
    listStyleType: "disc",
  },

  "& ol li[data-list='ordered']": {
    listStyleType: "decimal",
  },

  /* ===== BLOCKQUOTE ===== */
  "& blockquote": {
    borderLeft: "4px solid #3b82f6",
    padding: "10px 16px",
    background: "#f8fafc",
    color: "#374151",
    margin: "20px 0",
    fontStyle: "italic",
    borderRadius: 6,
  },

  /* ===== IMAGE ===== */
  "& img": {
    maxWidth: "100%",
    height: "auto",
    borderRadius: 12,
    margin: "24px 0",
    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
  },

  /* ===== LINK ===== */
  "& a": {
    color: "#2563eb",
    fontWeight: 500,
    textDecoration: "none",
    borderBottom: "1px solid rgba(37, 99, 235, 0.4)",
    transition: "all 0.2s ease",
  },

  "& a:hover": {
    color: "#1d4ed8",
    borderBottom: "1px solid #1d4ed8",
  },

  /* ===== CODE BLOCK ===== */
  "& pre": {
    background: "#0f172a",
    color: "#e2e8f0",
    padding: 16,
    borderRadius: 10,
    overflowX: "auto",
    margin: "20px 0",
  },

  "& code": {
    background: "#f1f5f9",
    padding: "2px 6px",
    borderRadius: 6,
    fontSize: 14,
  },

  /* ===== ALIGN (QUILL) ===== */
  "& .ql-align-center": {
    textAlign: "center",
  },

  "& .ql-align-right": {
    textAlign: "right",
  },

  "& .ql-align-justify": {
    textAlign: "justify",
  },

  /* ===== HR ===== */
  "& hr": {
    border: "none",
    borderTop: "1px solid #e5e7eb",
    margin: "32px 0",
  },

  /* ===== RESPONSIVE ===== */
  "@media (max-width: 768px)": {
    padding: "24px 16px",

    "& h1": { fontSize: 26 },
    "& h2": { fontSize: 22 },
    "& h3": { fontSize: 18 },
  },
}));

export const DocumentNoteHeader = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 16,
}));

export const DocumentNoteTitle = styled("h4")(() => ({
  margin: 0,
  fontSize: 18,
  fontWeight: 700,
  color: "#111827",
}));

export const DocumentNoteDesc = styled("p")(() => ({
  margin: "6px 0 0",
  fontSize: 14,
  color: "#6b7280",
}));

export const DocumentNoteTextarea = styled("textarea")(() => ({
  width: "100%",
  minHeight: 120,
  padding: "14px 16px",
  borderRadius: 12,
  border: "1px solid #d1d5db",
  backgroundColor: "#f9fafb",
  fontSize: 15,
  resize: "vertical",
  fontFamily: "inherit",
  transition: "all 0.2s ease",

  "::placeholder": {
    color: "#9ca3af",
  },

  "&:focus": {
    outline: "none",
    borderColor: "#9333ea",
    backgroundColor: "#ffffff",
    boxShadow: "0 0 0 3px rgba(147,51,234,0.15)",
  },
}));

export const DocumentNoteFooter = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: 12,
}));

export const DocumentNoteHint = styled("span")(() => ({
  fontSize: 13,
  color: "#6b7280",
}));

export const DocumentNoteCounter = styled("span")(() => ({
  fontSize: 13,
  fontWeight: 500,
  color: "#2563eb",
}));

export const FileInfoCard = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 14px",
  borderRadius: 10,
  background: "#f9fafb",
  border: "1px solid #eee",
  marginBottom: 20,
  justifyContent: "center",
});

export const FileIcon = styled("div")({
  fontSize: 22,
});

export const FileName = styled("div")({
  fontWeight: 600,
  fontSize: 14,
  color: "#111827",
  maxWidth: 250,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export const FileSize = styled("div")({
  fontSize: 12,
  color: "#6b7280",
  textAlign: "left",
});

export const SpinnerWrapper = styled("div")({
  margin: "20px 0",
});

export const ProcessText = styled("div")({
  fontSize: 15,
  fontWeight: 500,
  color: "#111827",
  marginBottom: "0.5rem",
});

export const SubProcessText = styled("div")({
  fontSize: 13,
  color: "#6b7280",
  marginTop: 6,
});

export const PercentText = styled("div")({
  marginTop: 6,
  fontSize: 13,
  color: "#6b7280",
});

export const RadioButton = styled("div")(() => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginTop: "0.5rem",
  "& input[type='radio']": {
    display: "none",
  },

  "& label": {
    marginTop: "2px",
    position: "relative",
    width: "16px", // nhỏ lại cho gọn
    height: "16px",
    borderRadius: "50%",
    cursor: "pointer",
    transition: "0.3s linear all",
    boxShadow: "0 0 4px rgba(0,0,0,0.2)",
    backgroundColor: "rgba(255,255,255,0.8)",
  },

  "& label:hover": {
    backgroundColor: "rgba(255,255,255,0.9)",
  },

  /* before + after khi checked */
  "& input[type='radio']:checked + label::before, & input[type='radio']:checked + label::after":
    {
      content: '""',
      position: "absolute",
      opacity: 0,
    },

  "& input[type='radio']:checked + label::before": {
    top: 0,
    left: 0,
    width: "16px",
    height: "16px",
    border: "2px solid transparent",
    borderRadius: "50%",
    opacity: 0,
    animation: "spin 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
    borderTop: "2px solid rgba(59,130,246,0.6)",
  },

  "& input[type='radio']:checked + label::after": {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    animation: "grow 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards",
    animationDelay: "0.3s",
    borderRadius: "50%",
    opacity: 1,
    backgroundColor: "rgba(59,130,246,0.7)",
  },

  "& span": {
    fontSize: "14px",
    color: "#334155",
  },

  /* animations */
  "@keyframes grow": {
    "0%": {
      width: "2px",
      height: "2px",
    },
    "100%": {
      width: "10px",
      height: "10px",
      opacity: 1,
    },
  },

  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
      opacity: 1,
    },
    "100%": {
      transform: "rotate(360deg)",
      opacity: 0,
    },
  },
}));

const processModalIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(18px) scale(0.96);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const processSpin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const processPulse = keyframes`
  0%, 100% {
    transform: scale(0.92);
    opacity: 0.55;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
`;

const processShine = keyframes`
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 44px 0;
  }
`;

export const ProcessModal = styled("div")(({ theme }) => ({
  width: "min(92vw, 520px)",
  borderRadius: 28,
  padding: 24,
  position: "relative",
  overflow: "hidden",
  background:
    "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,251,255,1) 100%)",
  boxShadow: "0 30px 90px rgba(15, 23, 42, 0.35)",
  animation: `${processModalIn} 0.28s ease-out`,

  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "radial-gradient(circle at 20% 0%, rgba(147,51,234,0.16), transparent 34%), radial-gradient(circle at 90% 10%, rgba(37,99,235,0.16), transparent 30%)",
  },

  "& > *": {
    position: "relative",
    zIndex: 1,
  },

  [theme.breakpoints.down("sm")]: {
    padding: 18,
    borderRadius: 22,
  },
}));

export const ProcessFileCard = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: "14px 16px",
  borderRadius: 18,
  background: "rgba(255,255,255,0.86)",
  border: "1px solid rgba(226,232,240,0.95)",
  boxShadow: "0 12px 30px rgba(15,23,42,0.06)",
  marginBottom: 18,
});

export const ProcessFileExt = styled("div")({
  width: 52,
  height: 52,
  borderRadius: 16,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#ffffff",
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: "0.04em",
  background: "linear-gradient(135deg, #9333ea 0%, #2563eb 100%)",
  boxShadow: "0 14px 26px rgba(79,70,229,0.28)",
});

export const ProcessLoader = styled("div")({
  width: 92,
  height: 92,
  margin: "20px auto 18px",
  borderRadius: "50%",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: "4px solid rgba(147,51,234,0.12)",
    borderTopColor: "#9333ea",
    borderRightColor: "#2563eb",
    animation: `${processSpin} 1s linear infinite`,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    inset: 12,
    borderRadius: "50%",
    border: "3px dashed rgba(37,99,235,0.28)",
    animation: `${processSpin} 2.4s linear infinite reverse`,
  },

  "& span": {
    width: 9,
    height: 9,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #9333ea 0%, #2563eb 100%)",
    position: "absolute",
    animation: `${processPulse} 1.2s ease-in-out infinite`,
  },

  "& span:nth-of-type(1)": {
    top: 17,
    animationDelay: "0s",
  },

  "& span:nth-of-type(2)": {
    right: 17,
    animationDelay: "0.15s",
  },

  "& span:nth-of-type(3)": {
    bottom: 17,
    animationDelay: "0.3s",
  },

  "& i": {
    width: 42,
    height: 42,
    borderRadius: 14,
    display: "block",
    background:
      "linear-gradient(135deg, rgba(147,51,234,0.12), rgba(37,99,235,0.12))",
    position: "relative",
  },

  "& i::before": {
    content: '"AI"',
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#4f46e5",
    fontWeight: 600,
    fontSize: 13,
  },
});

export const ProcessProgressHead = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  marginTop: 16,
  marginBottom: 8,
  fontSize: 13,
  color: "#64748b",

  "& strong": {
    color: "#4f46e5",
    fontSize: 14,
  },
});

export const ProcessProgressBar = styled("div")({
  height: 13,
  width: "100%",
  borderRadius: 999,
  background: "#e5e7eb",
  overflow: "hidden",
  boxShadow: "inset 0 1px 3px rgba(15,23,42,0.08)",
});

export const ProcessProgressFill = styled("div")<{ done?: boolean }>(
  ({ done }) => ({
    height: "100%",
    borderRadius: 999,
    background: done
      ? "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)"
      : "repeating-linear-gradient(135deg, #9333ea 0px, #9333ea 11px, #6366f1 11px, #6366f1 22px, #2563eb 22px, #2563eb 33px)",
    backgroundSize: "44px 44px",
    animation: done ? "none" : `${processShine} 0.9s linear infinite`,
    transition: "width 0.35s ease",
    boxShadow: done
      ? "0 0 12px rgba(34,197,94,0.45)"
      : "0 0 12px rgba(99,102,241,0.45)",
  }),
);

export const ProcessStageDots = styled("div")({
  display: "flex",
  justifyContent: "center",
  gap: 8,
  marginTop: 14,

  "& span": {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#cbd5e1",
    transition: "all 0.25s ease",
  },

  "& span.active": {
    width: 22,
    borderRadius: 999,
    background: "linear-gradient(135deg, #9333ea 0%, #2563eb 100%)",
    boxShadow: "0 4px 10px rgba(79,70,229,0.28)",
  },
});

export const ProcessTip = styled("div")({
  fontSize: 13,
  color: "red",
  marginTop: 10,
  lineHeight: 1.5,
});

/* ===== Upload file selected UI - modern card style ===== */
export const FileBlock = styled("div")(({ theme }) => ({
  marginTop: 24,

  [theme.breakpoints.down("sm")]: {
    marginTop: 18,
  },
}));

export const UploadedFileCard = styled("div")(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  minHeight: 108,
  display: "flex",
  alignItems: "center",
  gap: 22,
  padding: "0px 34px",
  borderRadius: 18,
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(250,252,255,0.95) 100%)",
  border: "1px solid rgba(226, 232, 240, 0.95)",
  boxShadow:
    "0 16px 42px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.05)",

  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background:
      "radial-gradient(circle at 12% 18%, rgba(239, 68, 68, 0.08), transparent 24%), radial-gradient(circle at 88% 12%, rgba(37, 99, 235, 0.06), transparent 30%)",
  },

  "&::after": {
    content: '""',
    position: "absolute",
    right: -18,
    bottom: -54,
    width: 650,
    height: 170,
    pointerEvents: "none",
    opacity: 0.72,
    background:
      "repeating-radial-gradient(ellipse at 50% 100%, transparent 0 10px, rgba(239, 68, 68, 0.13) 11px 12px, transparent 13px 20px)",
    transform: "rotate(-5deg)",
  },

  [theme.breakpoints.down("sm")]: {
    alignItems: "flex-start",
    gap: 14,
    padding: "18px",
    flexWrap: "wrap",
  },
}));

export const UploadedFileIcon = styled("div")(() => ({
  position: "relative",
  zIndex: 1,
  width: 76,
  height: 76,
  flex: "0 0 76px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 16,
  background: "linear-gradient(135deg, #fff1f2 0%, #fff7f7 100%)",
  boxShadow: "inset 0 0 0 1px rgba(239, 68, 68, 0.06)",

  "&::before": {
    content: '""',
    position: "absolute",
    width: 32,
    height: 42,
    border: "3px solid #ef233c",
    borderRadius: 5,
    background: "#ffffff",
    boxShadow: "0 8px 18px rgba(239, 35, 60, 0.13)",
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 24,
    right: 24,
    width: 10,
    height: 10,
    borderTop: "3px solid #ef233c",
    borderRight: "3px solid #ef233c",
    borderTopRightRadius: 3,
    background: "#fff1f2",
  },

  "& span": {
    position: "relative",
    zIndex: 2,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 28,
    height: 15,
    marginTop: 22,
    borderRadius: 3,
    background: "#ef233c",
    color: "#ffffff",
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: 0.2,
    lineHeight: 1,
  },
}));

export const UploadedFileMeta = styled("div")(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  minWidth: 0,
  flex: 1,
  display: "flex",
  alignItems: "baseline",
  gap: 12,

  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 4,
    width: "calc(100% - 92px)",
  },
}));

export const UploadedFileName = styled("strong")(({ theme }) => ({
  display: "block",
  maxWidth: "min(620px, 58vw)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "#0f172a",
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: "-0.02em",

  [theme.breakpoints.down("sm")]: {
    maxWidth: "100%",
    fontSize: 16,
  },
}));

export const UploadedFileSize = styled("span")(() => ({
  flex: "0 0 auto",
  color: "#64748b",
  fontSize: 15,
  fontWeight: 500,
}));

export const UploadedFileActions = styled("div")(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 10,
  marginLeft: "auto",

  [theme.breakpoints.down("sm")]: {
    width: "100%",
    marginLeft: 0,
    justifyContent: "flex-start",
  },
}));

export const UploadedPreviewButton = styled("button")(() => ({
  height: 40,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 7,
  padding: "0 16px",
  borderRadius: 10,
  border: "1px solid rgba(37, 99, 235, 0.25)",
  background: "rgba(239, 246, 255, 0.92)",
  color: "#2563eb",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  transition: "all .22s ease",

  "&:hover": {
    transform: "translateY(-1px)",
    background: "#eff6ff",
    boxShadow: "0 10px 22px rgba(37, 99, 235, 0.12)",
  },
}));

export const UploadedDeleteButton = styled("button")(() => ({
  height: 40,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  padding: "0 17px",
  borderRadius: 10,
  border: "1px solid #ff3b4a",
  background: "rgba(255, 255, 255, 0.82)",
  color: "#ef233c",
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
  transition: "all .22s ease",

  "&:hover": {
    transform: "translateY(-1px)",
    background: "#fff1f2",
    boxShadow: "0 10px 24px rgba(239, 35, 60, 0.15)",
  },
}));

export const ToneSectionTitle = styled("div")(({ theme }) => ({
  margin: "26px 0 12px 14px",
  color: "#0f172a",
  fontSize: 19,
  fontWeight: 600,

  [theme.breakpoints.down("sm")]: {
    marginLeft: 2,
    fontSize: 17,
  },
}));

export const ToneListCard = styled("div")(() => ({
  overflow: "hidden",
  borderRadius: 16,
  border: "1px solid rgba(226, 232, 240, 0.95)",
  background: "rgba(255, 255, 255, 0.96)",
  boxShadow: "0 14px 36px rgba(15, 23, 42, 0.08)",
}));

export const ToneChoice = styled("label", {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ selected }) => ({
  minHeight: 70,
  display: "flex",
  alignItems: "center",
  gap: 16,
  padding: "0 24px",
  cursor: "pointer",
  borderBottom: "1px solid #e5e7eb",
  background: selected
    ? "linear-gradient(90deg, rgba(239, 246, 255, 0.82), rgba(255,255,255,0.98))"
    : "#ffffff",
  transition: "background .18s ease, box-shadow .18s ease",

  "&:last-of-type": {
    borderBottom: "none",
  },

  "&:hover": {
    background: selected ? "#eff6ff" : "#f8fafc",
  },

  "& input": {
    position: "absolute",
    opacity: 0,
    pointerEvents: "none",
  },

  "& .radio-dot": {
    position: "relative",
    width: 15,
    height: 15,
    flex: "0 0 15px",
    borderRadius: "50%",
    border: selected ? "2px solid #2f80ff" : "2px solid #cbd5e1",
    background: "#ffffff",
    boxShadow: selected ? "0 0 0 4px rgba(47, 128, 255, 0.12)" : "none",
  },

  "& .radio-dot::after": {
    content: '""',
    position: "absolute",
    inset: 4,
    borderRadius: "50%",
    background: selected ? "#2f80ff" : "transparent",
  },
}));

export const ToneChoiceIcon = styled("span")(() => ({
  width: 48,
  height: 48,
  flex: "0 0 48px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  fontSize: 24,
  fontWeight: 800,

  "&.tone-friendly": {
    background: "#eaf2ff",
    color: "#2563eb",
  },

  "&.tone-inspiring": {
    background: "#fff1f2",
    color: "#ef4444",
  },

  "&.tone-neutral": {
    background: "#eef2f7",
    color: "#64748b",
  },

  "&.tone-professional": {
    background: "#f3e8ff",
    color: "#9333ea",
  },

  "&.tone-formal": {
    background: "#eaf2ff",
    color: "#2563eb",
  },
}));

export const ToneChoiceText = styled("span")(({ theme }) => ({
  color: "#1f2937",
  fontSize: 14,
  fontWeight: 500,

  [theme.breakpoints.down("sm")]: {
    fontSize: 13,
  },
}));

export const TermsAgreementBox = styled("div", {
  shouldForwardProp: (prop) => prop !== "accepted",
})<{ accepted?: boolean }>(({ accepted, theme }) => ({
  marginTop: 24,
  padding: "14px 16px",
  borderRadius: 14,
  border: accepted ? "1px solid rgba(47, 128, 255, 0.38)" : "1px solid #bfdbfe",
  background: accepted
    ? "linear-gradient(135deg, #eff6ff 0%, #ffffff 100%)"
    : "rgba(255, 255, 255, 0.9)",
  boxShadow: "0 10px 26px rgba(37, 99, 235, 0.07)",

  [theme.breakpoints.down("sm")]: {
    padding: 12,
  },
}));

export const TermsAgreementLabel = styled("label")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 14,
  cursor: "pointer",
  color: "#172554",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.55,

  "& input": {
    width: 18,
    height: 18,
    flex: "0 0 18px",
    margin: 0,
    accentColor: "#2f80ff",
    cursor: "pointer",
  },

  [theme.breakpoints.down("sm")]: {
    alignItems: "flex-start",
    gap: 10,
    fontSize: 13,
  },
}));

export const TermsAgreementIcon = styled("span")(() => ({
  width: 42,
  height: 42,
  flex: "0 0 42px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 12,
  background: "#eff6ff",
  color: "#2563eb",
}));

export const TermsText = styled("span")(() => ({
  color: "#172554",
  fontWeight: 600,
  "& strong, & b, & a": {
    color: "#ef233c",
    fontWeight: 600,
    textDecoration: "none",
  },

  "& a:hover": {
    textDecoration: "underline",
  },
}));

export const TranslateNowButton = styled("button")(({ theme }) => ({
  // minWidth: 360,
  height: 50,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 14,
  padding: "0 42px",
  border: "none",
  borderRadius: 12,
  background: "linear-gradient(135deg, #a855f7 0%, #2563eb 100%)",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: "0.02em",
  textTransform: "uppercase",
  cursor: "pointer",
  boxShadow:
    "0 16px 34px rgba(37, 99, 235, 0.28), 0 6px 16px rgba(168, 85, 247, 0.18)",
  transition: "transform .22s ease, box-shadow .22s ease, opacity .22s ease",

  "&:hover:not(:disabled)": {
    transform: "translateY(-2px)",
    boxShadow:
      "0 20px 42px rgba(37, 99, 235, 0.34), 0 8px 20px rgba(168, 85, 247, 0.22)",
  },

  "&:disabled": {
    opacity: 0.55,
    cursor: "not-allowed",
    transform: "none",
    boxShadow: "none",
    background: "linear-gradient(135deg, #c4b5fd 0%, #93c5fd 100%)",
  },

  [theme.breakpoints.down("sm")]: {
    width: "100%",
    minWidth: "unset",
    height: 56,
    fontSize: 16,
  },
}));
